import React, { useState } from 'react'
import {
  Combo,
  DataGridEditableCell,
  editableActions,
  editableColumn,
  enumToComboOptionTransformer,
} from '../../../core/component'
import { confirm, notify, translate } from '../../../core'
import { Flex, Form, Input, message, Space, Table } from 'antd'
import { FilterOutlined } from '@ant-design/icons'
import { ItemCategoryLevel, ItemCategoryListItem } from '../type'
import { useMutation, useQuery } from '@apollo/client'
import { useItemCategoryData } from '../hook/item-category-data-hook'
import {
  GetItemCategoryList,
  ItemCategoryDeleteMutation,
  ItemCategoryUpdateMutation
} from '../graphql/item-category.graphql'
import { client } from '../../../core/query'
import { SortDirectionOptions, SortItem } from '../../../core/component/Search'
import style from './ItemCategoryList.module.scss'

const sortMapper: Record<string, SortDirectionOptions> = {
  'ascend': 'asc',
  'descend': 'desc',
}

export const ItemCategoryListAdmin = () => {
  const [form] = Form.useForm()
  const [messageApi, contextHolder] = message.useMessage()
  const {remove} = useItemCategoryData()
  const [search, setSearch] = useState<string>()
  const [level, setLevel] = useState<ItemCategoryLevel>()
  const [pageSize, setPageSize] = useState<number>(10)
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [editingKey, setEditingKey] = useState<string>('')
  const isEditing = (record: ItemCategoryListItem) => record.key === editingKey;
  const [sort, setSort] = useState<SortItem[]>()
  const {data, loading} = useQuery(GetItemCategoryList, {
    variables: {
      request: {
        take: pageSize,
        skip: currentPage * pageSize,
        level,
        title: search,
        sort,
      }
    }
  })
  const [update, {loading: isUpdating}] = useMutation(ItemCategoryUpdateMutation, {
    refetchQueries: [GetItemCategoryList],
    onCompleted: async () => {
      messageApi.success(translate('item_category', 'save_success_message'))
      setEditingKey('')
    },
    onError: (err) => messageApi.error(err.message),
  })

  const [deleteRow, {loading: deleting}] = useMutation(ItemCategoryDeleteMutation, {
    refetchQueries: [GetItemCategoryList],
    onCompleted: async () => {
      messageApi.success(translate('item_category', 'remove_success_message'))
      setEditingKey('')
    },
    onError: (err) => messageApi.error(err.message),
  })

  return (
    <div>
      <Flex className='p-2 mb-3'>
        <Flex className='w-100' justify='start' align='start'>
          <Space>
            <FilterOutlined/>
            <Combo
              placeholder={translate('category')}
              style={{width: 200}}
              onChange={value => {
                setLevel(value as ItemCategoryLevel)
              }}
              options={enumToComboOptionTransformer(ItemCategoryLevel)}
            />

            <Input
              placeholder={translate('title')}
              onChange={e => {
                setSearch(e.currentTarget.value)
              }}
            />
          </Space>
        </Flex>
      </Flex>

      <Form form={form} component={false}>
        <Table<ItemCategoryListItem>
          loading={loading}
          columns={[
            {
              dataIndex: 'id',
              title: translate('id'),
              width: 100,
            },
            editableColumn<ItemCategoryListItem>({
              dataIndex: 'title',
              title: translate('title'),
              editableComponent:
                <Form.Item
                  name='title'
                  rules={[{
                    required: true,
                    message: translate('missing_field_message', 'title')
                  }]}>
                  <Input/>
                </Form.Item>,
              isEditing,
            }),
            {
              dataIndex: 'level',
              title: translate('level'),
            },
            {
              dataIndex: 'used',
              title: translate('used'),
              sorter: true,
            },
            editableActions(
              editingKey,
              isUpdating,
              isEditing,
              () => setEditingKey(''),
              it => {
                form.setFieldsValue(it)
                setEditingKey(it.key ?? '')
              },
              async it => {
                const row = await form.validateFields() as ItemCategoryListItem
                await update({
                  variables: {
                    id: it.id,
                    input: {
                      title: row.title,
                    }
                  }
                })
              },
              async it => {
                const result = await confirm(translate('remove_confirmation_message', 'item_category', '?'))

                if (!result)
                  return

                await deleteRow({
                  variables: {
                    id: it.id
                  }
                })
              },
              deleting,
            ),
          ]}
          dataSource={(data?.itemCategoryListFind.data ?? []).map(it => ({...it, key: it.id.toString()}))}
          pagination={{
            total: data?.itemCategoryListFind.count,
            position: ['bottomCenter'],
            pageSize: pageSize,
            showSizeChanger: true,
          }}
          onChange={(pagination, _, sorter) => {
            setEditingKey('')
            if (pagination.current)
              setCurrentPage(pagination.current - 1)

            if (pagination.pageSize)
              setPageSize(pagination.pageSize)

            if (sorter)
              setSort(
                [sorter].flat()
                  .filter(({field}) => !!field)
                  .map(({field, order}) => ({
                    field: field?.toString() ?? '',
                    direction: sortMapper[order ?? ''],
                  }))
              )
          }}
          rowClassName={style.editableRow}
          components={{
            body: {cell: DataGridEditableCell},
          }}
        />
      </Form>
      {contextHolder}
    </div>
  )
}
